import React, { useState } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import { useToastStore } from '../stores/useToastStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import { klineSignalService } from '../services/klineSignalService';
import type { SignalScannerResult } from '../services/klineTypes';
import PageHeader from '../components/PageHeader';
import { ScannerTimeFrame } from '@/services/enums';

const SignalScanner: React.FC = () => {
  const setCurrentPage = useSetCurrentPage();
  const showToast = useToastStore((state) => state.show);
  const [loading, setLoading] = useState(false);
  const [backtesting, setBacktesting] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  // const backtestPollCleanupRef = useRef<(() => void) | null>(null); // Unused variable
  // const optimizePollCleanupRef = useRef<(() => void) | null>(null); // Unused variable
  const [signals, setSignals] = useState<SignalScannerResult[]>([]);
  const [backtestResult, setBacktestResult] = useState<{
    taskId: string;
    status: string;
    message: string;
    result?: {
      totalSignals: number;
      profitableSignals: number;
      successRate: string;
    };
  } | null>(null);
  const [optimizeResult, setOptimizeResult] = useState<{
    taskId: string;
    status: string;
    message: string;
    result?: {
      bestParams: any;
      bestSuccessRate: string;
      iterations: number;
      optimizationHistory: Array<{
        iteration: number;
        params: any;
        successRate: string;
      }>;
    };
  } | null>(null);

  // 时间范围配置（默认最近14天）
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 14);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() + 1); // 明天
    return date.toISOString().split('T')[0];
  });

  // 信号类型配置
  const [signalType, setSignalType] = useState<'ABSORPTION'>('ABSORPTION');

  // 时间粒度配置
  const [bar, setBar] = useState<ScannerTimeFrame>('1H');

  // 参数配置
  const [rsiPeriod, setRsiPeriod] = useState<number>(14);
  const [lookbackWindow, setLookbackWindow] = useState<number>(20);
  const [priceSlopeMin, setPriceSlopeMin] = useState<number>(1);
  const [priceSlopeMax, setPriceSlopeMax] = useState<number>(6);
  const [rsiSlopeMin, setRsiSlopeMin] = useState<number | undefined>(1);
  const [rsiSlopeMax, setRsiSlopeMax] = useState<number | undefined>(3);
  const [volumeSlopeMin, setVolumeSlopeMin] = useState<number | undefined>(1);
  const [volumeSlopeMax, setVolumeSlopeMax] = useState<number | undefined>(3);
  const [obvSlopeMin, setObvSlopeMin] = useState<number | undefined>(0);
  const [priceAboveEma, setPriceAboveEma] = useState<boolean>(false);
  const [rsiMin, setRsiMin] = useState<number | undefined>(0); // RSI最小值（默认0，表示不限制）
  const [rsiMax, setRsiMax] = useState<number | undefined>(100); // RSI最大值（默认100，表示不限制）
  const [bbWidthMax, setBbWidthMax] = useState<number | undefined>(0.15); // 布林带宽度最大值（默认0.15，即15%）
  const [volumeRatioMin, setVolumeRatioMin] = useState<number | undefined>(1.5); // 成交量比率最小值（默认1.5倍）
  const [maxVolatility, setMaxVolatility] = useState<number>(0.05); // 最大波动率（5%）
  const [maxGapHours, setMaxGapHours] = useState<number>(5);

  // 格式化时间戳为北京时间
  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    const beijingTime = new Date(date.getTime() + 8 * 60 * 60 * 1000);
    return beijingTime.toISOString().replace('T', ' ').substring(0, 19) + ' (UTC+8)';
  };

  // 扫描信号
  const handleScan = async () => {
    try {
      setLoading(true);
      setSignals([]);

      // 将日期转换为时间戳（秒）
      const startTime = Math.floor(new Date(startDate).getTime() / 1000);
      const endTime = Math.floor(new Date(endDate + 'T23:59:59').getTime() / 1000);

      if (startTime >= endTime) {
        showToast('开始时间必须小于结束时间', 'error');
        setLoading(false);
        return;
      }

      const response = await klineSignalService.scanSignals({
        startTime,
        endTime,
        bar,
        signalType,
        rsiPeriod,
        lookbackWindow,
        priceSlopeMin,
        priceSlopeMax,
        rsiSlopeMin,
        rsiSlopeMax,
        volumeSlopeMin,
        volumeSlopeMax,
        maxVolatility,
        maxGapHours,
        obvSlopeMin,
        priceAboveEma,
        rsiMin,
        rsiMax,
        bbWidthMax,
        volumeRatioMin,
      });

      if (response) {
        setSignals(response.signals);
        showToast(`扫描完成，找到 ${response.total} 个信号`, 'success');
      } else {
        showToast('扫描失败', 'error');
      }
    } catch (error: any) {
      console.error('扫描信号失败:', error);
      showToast(error.message || '扫描信号失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 回测功能
  const handleBacktest = async () => {
    try {
      setBacktesting(true);
      setBacktestResult(null);

      // 将日期转换为时间戳（秒）
      const startTime = Math.floor(new Date(startDate).getTime() / 1000);
      const endTime = Math.floor(new Date(endDate + 'T23:59:59').getTime() / 1000);

      if (startTime >= endTime) {
        showToast('开始时间必须小于结束时间', 'error');
        setBacktesting(false);
        return;
      }

      const response = await klineSignalService.startBacktest({
        startTime,
        endTime,
        bar,
        signalType,
        rsiPeriod,
        lookbackWindow,
        priceSlopeMin,
        priceSlopeMax,
        rsiSlopeMin,
        rsiSlopeMax,
        volumeSlopeMin,
        volumeSlopeMax,
        maxVolatility,
        maxGapHours,
        obvSlopeMin,
        priceAboveEma,
        rsiMin,
        rsiMax,
        bbWidthMax,
        volumeRatioMin,
      });

      if (response) {
        const taskId = response.taskId;
        setBacktestResult({
          taskId,
          status: response.status,
          message: response.message,
        });
        showToast('回测任务已创建，正在处理中...', 'success');

        // 开始轮询任务状态
        pollBacktestStatus(taskId);
      } else {
        showToast('创建回测任务失败', 'error');
      }
    } catch (error: any) {
      console.error('回测失败:', error);
      showToast(error.message || '回测失败', 'error');
    } finally {
      setBacktesting(false);
    }
  };

  // 轮询回测任务状态
  const pollBacktestStatus = async (taskId: string) => {
    const maxAttempts = 300; // 最多轮询300次（5分钟，每次1秒）
    let attempts = 0;
    let pollTimer: NodeJS.Timeout | null = null;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setBacktesting(false);
        setBacktestResult((prev) =>
          prev
            ? {
                ...prev,
                status: 'timeout',
                message: '回测任务超时（超过5分钟）',
              }
            : null,
        );
        showToast('回测任务超时（超过5分钟）', 'error');
        return;
      }

      try {
        const response = await klineSignalService.getBacktestStatus(taskId);

        if (response) {
          const task = response;

          setBacktestResult({
            taskId: task.taskId,
            status: task.status,
            message:
              task.status === 'completed'
                ? `回测完成：成功率 ${task.result?.successRate || '0%'}`
                : task.status === 'failed'
                  ? `回测失败：${task.error || '未知错误'}`
                  : `回测任务处理中... (${attempts + 1}/${maxAttempts})`,
            result: task.result,
          });

          if (task.status === 'completed' || task.status === 'failed') {
            // 任务完成或失败，停止轮询
            setBacktesting(false);
            if (task.status === 'completed') {
              showToast(`回测完成：成功率 ${task.result?.successRate || '0%'}`, 'success');
            } else {
              showToast(`回测失败：${task.error || '未知错误'}`, 'error');
            }
            return;
          }

          // 任务仍在处理中，继续轮询
          attempts++;
          pollTimer = setTimeout(poll, 1000); // 1秒后再次轮询
        } else {
          // 响应失败，但继续尝试
          attempts++;
          if (attempts < maxAttempts) {
            pollTimer = setTimeout(poll, 1000);
          } else {
            setBacktesting(false);
            setBacktestResult((prev) =>
              prev
                ? {
                    ...prev,
                    status: 'error',
                    message: '获取回测任务状态失败',
                  }
                : null,
            );
            showToast('获取回测任务状态失败', 'error');
          }
        }
      } catch (error: any) {
        console.error('获取回测任务状态失败:', error);
        attempts++;
        if (attempts < maxAttempts) {
          pollTimer = setTimeout(poll, 1000);
        } else {
          setBacktesting(false);
          setBacktestResult((prev) =>
            prev
              ? {
                  ...prev,
                  status: 'error',
                  message: `获取回测任务状态失败：${error.message || '未知错误'}`,
                }
              : null,
          );
          showToast('获取回测任务状态失败，已停止轮询', 'error');
        }
      }
    };

    // 开始轮询
    pollTimer = setTimeout(poll, 1000); // 1秒后开始第一次轮询

    // 返回清理函数，以便在组件卸载时停止轮询
    return () => {
      if (pollTimer) {
        clearTimeout(pollTimer);
      }
      setBacktesting(false);
    };
  };

  // 优化功能
  const handleOptimize = async () => {
    try {
      setOptimizing(true);
      setOptimizeResult(null);

      // 将日期转换为时间戳（秒）
      const startTime = Math.floor(new Date(startDate).getTime() / 1000);
      const endTime = Math.floor(new Date(endDate + 'T23:59:59').getTime() / 1000);

      if (startTime >= endTime) {
        showToast('开始时间必须小于结束时间', 'error');
        setOptimizing(false);
        return;
      }

      const response = await klineSignalService.startOptimize({
        startTime,
        endTime,
        bar,
        signalType,
        initialParams: {
          rsiPeriod,
          lookbackWindow,
          priceSlopeMin,
          priceSlopeMax,
          rsiSlopeMin,
          rsiSlopeMax,
          volumeSlopeMin,
          volumeSlopeMax,
          maxVolatility,
          maxGapHours,
          obvSlopeMin,
          priceAboveEma,
          rsiMin,
          rsiMax,
          bbWidthMax,
          volumeRatioMin,
        },
      });

      if (response) {
        const taskId = response.taskId;
        setOptimizeResult({
          taskId,
          status: response.status,
          message: response.message,
        });
        showToast('优化任务已创建，正在处理中...', 'success');

        // 开始轮询任务状态（保持 optimizing 为 true）
        pollOptimizeStatus(taskId);
        // 注意：不在 finally 中设置 setOptimizing(false)，让轮询函数在任务完成时设置
      } else {
        showToast('创建优化任务失败', 'error');
        setOptimizing(false);
      }
    } catch (error: any) {
      console.error('优化失败:', error);
      showToast(error.message || '优化失败', 'error');
      setOptimizing(false);
    }
  };

  // 轮询优化任务状态
  const pollOptimizeStatus = async (taskId: string) => {
    const maxAttempts = 600; // 最多轮询600次（10分钟，每次1秒）
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setOptimizing(false);
        showToast('优化任务超时', 'error');
        return;
      }

      try {
        const response = await klineSignalService.getOptimizeStatus(taskId);

        if (response) {
          const task = response;

          setOptimizeResult({
            taskId: task.taskId,
            status: task.status,
            message:
              task.status === 'completed'
                ? `优化完成：最佳成功率 ${task.result?.bestSuccessRate || '0%'}`
                : task.status === 'failed'
                  ? `优化失败：${task.error || '未知错误'}`
                  : `优化中... (迭代 ${task.result?.iterations || 0})`,
            result: task.result,
          });

          if (task.status === 'completed' || task.status === 'failed') {
            // 任务完成或失败，停止轮询
            setOptimizing(false);
            if (task.status === 'completed') {
              showToast(`优化完成：最佳成功率 ${task.result?.bestSuccessRate || '0%'}`, 'success');
              // 自动更新参数为最佳参数（只更新被优化的参数）
              if (task.result?.bestParams) {
                const best = task.result.bestParams;
                setPriceSlopeMin(best.priceSlopeMin);
                setPriceSlopeMax(best.priceSlopeMax);
                setRsiSlopeMin(best.rsiSlopeMin);
                setRsiSlopeMax(best.rsiSlopeMax);
                setVolumeSlopeMin(best.volumeSlopeMin);
                setVolumeSlopeMax(best.volumeSlopeMax);
                if (best.obvSlopeMin !== undefined) {
                  setObvSlopeMin(best.obvSlopeMin);
                }
                if (best.priceAboveEma !== undefined) {
                  setPriceAboveEma(best.priceAboveEma);
                }
                if (best.rsiMin !== undefined) {
                  setRsiMin(best.rsiMin);
                }
                if (best.rsiMax !== undefined) {
                  setRsiMax(best.rsiMax);
                }
                if (best.bbWidthMax !== undefined) {
                  setBbWidthMax(best.bbWidthMax);
                }
                if (best.volumeRatioMin !== undefined) {
                  setVolumeRatioMin(best.volumeRatioMin);
                }
                showToast('优化后的参数已自动更新', 'success');
              }
            } else {
              showToast(`优化失败：${task.error || '未知错误'}`, 'error');
            }
            return;
          }

          // 任务仍在处理中，继续轮询
          attempts++;
          setTimeout(poll, 1000); // 1秒后再次轮询
        }
      } catch (error: any) {
        console.error('获取优化任务状态失败:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          setOptimizing(false);
        }
      }
    };

    // 开始轮询
    setTimeout(poll, 1000); // 1秒后开始第一次轮询
  };

  return (
    <GradientBackground>
      <div className="min-h-screen">
        <ParticleBackground />

        {/* 页面头部 */}
        <PageHeader title="信号嗅探" onBack={() => setCurrentPage('personal-center')} />

        {/* 内容区域 */}
        <div className="px-4 py-4 pt-20">
          {/* 配置区域 */}
          <div className="mb-6 space-y-4">
            {/* 时间范围 */}
            <div className="bg-white/5 border border-white/20 rounded-xl p-4">
              <h2 className="text-white/90 font-semibold mb-3">时间范围</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">开始日期</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">结束日期</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                  />
                </div>
              </div>
            </div>

            {/* 信号类型和时间粒度 */}
            <div className="bg-white/5 border border-white/20 rounded-xl p-4">
              <h2 className="text-white/90 font-semibold mb-3">信号类型</h2>
              <select
                value={signalType}
                onChange={(e) => setSignalType(e.target.value as 'ABSORPTION')}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 mb-3"
              >
                <option value="ABSORPTION" className="bg-gray-800 text-white">
                  强力吸筹
                </option>
              </select>
              <h2 className="text-white/90 font-semibold mb-3">时间粒度</h2>
              <select
                value={bar}
                onChange={(e) => setBar(e.target.value as ScannerTimeFrame)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40"
              >
                <option value="1H" className="bg-gray-800 text-white">
                  1小时
                </option>
                <option value="4H" className="bg-gray-800 text-white">
                  4小时
                </option>
                <option value="1D" className="bg-gray-800 text-white">
                  1天
                </option>
              </select>
            </div>

            {/* 参数配置 */}
            <div className="bg-white/5 border border-white/20 rounded-xl p-4">
              <h2 className="text-white/90 font-semibold mb-3">参数配置</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">RSI周期</label>
                  <input
                    type="number"
                    value={rsiPeriod}
                    onChange={(e) => setRsiPeriod(Number(e.target.value))}
                    min="1"
                    max="50"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">回看窗口</label>
                  <input
                    type="number"
                    value={lookbackWindow}
                    onChange={(e) => setLookbackWindow(Number(e.target.value))}
                    min="1"
                    max="50"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">价格斜率最小值</label>
                  <input
                    type="number"
                    step="0.1"
                    value={priceSlopeMin}
                    onChange={(e) => setPriceSlopeMin(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">价格斜率最大值</label>
                  <input
                    type="number"
                    step="0.1"
                    value={priceSlopeMax}
                    onChange={(e) => setPriceSlopeMax(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">RSI斜率最小值</label>
                  <input
                    type="number"
                    step="0.1"
                    value={rsiSlopeMin ?? ''}
                    onChange={(e) => setRsiSlopeMin(e.target.value === '' ? undefined : Number(e.target.value))}
                    placeholder="不限制"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">RSI斜率最大值</label>
                  <input
                    type="number"
                    step="0.1"
                    value={rsiSlopeMax ?? ''}
                    onChange={(e) => setRsiSlopeMax(e.target.value === '' ? undefined : Number(e.target.value))}
                    placeholder="不限制"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">成交量斜率最小值</label>
                  <input
                    type="number"
                    step="0.1"
                    value={volumeSlopeMin ?? ''}
                    onChange={(e) => setVolumeSlopeMin(e.target.value === '' ? undefined : Number(e.target.value))}
                    placeholder="不限制"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">成交量斜率最大值</label>
                  <input
                    type="number"
                    step="0.1"
                    value={volumeSlopeMax ?? ''}
                    onChange={(e) => setVolumeSlopeMax(e.target.value === '' ? undefined : Number(e.target.value))}
                    placeholder="不限制"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">OBV斜率最小值</label>
                  <input
                    type="number"
                    step="0.1"
                    value={obvSlopeMin ?? 0}
                    onChange={(e) => setObvSlopeMin(e.target.value === '' ? 0 : Number(e.target.value))}
                    placeholder="0"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                  />
                  <div className="text-white/50 text-xs mt-1">要求OBV斜率必须大于等于此值（区分买盘和卖盘，默认0）</div>
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">最大波动率（%）</label>
                  <input
                    type="number"
                    step="0.01"
                    value={maxVolatility * 100}
                    onChange={(e) => setMaxVolatility(Number(e.target.value) / 100)}
                    min="0"
                    max="100"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                  />
                  <div className="text-white/50 text-xs mt-1">每根K线的波动率必须小于此值（默认5%）</div>
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">信号合并间隔（小时）</label>
                  <input
                    type="number"
                    value={maxGapHours}
                    onChange={(e) => setMaxGapHours(Number(e.target.value))}
                    min="0"
                    max="24"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">RSI最小值</label>
                  <input
                    type="number"
                    step="1"
                    value={rsiMin ?? ''}
                    onChange={(e) => setRsiMin(e.target.value === '' ? undefined : Number(e.target.value))}
                    placeholder="0（不限制）"
                    min="0"
                    max="100"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                  />
                  <div className="text-white/50 text-xs mt-1">RSI值必须大于等于此值（默认0，表示不限制）</div>
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">RSI最大值</label>
                  <input
                    type="number"
                    step="1"
                    value={rsiMax ?? ''}
                    onChange={(e) => setRsiMax(e.target.value === '' ? undefined : Number(e.target.value))}
                    placeholder="100（不限制）"
                    min="0"
                    max="100"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                  />
                  <div className="text-white/50 text-xs mt-1">RSI值必须小于等于此值（默认100，表示不限制）</div>
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">布林带宽度最大值（%）</label>
                  <input
                    type="number"
                    step="0.01"
                    value={bbWidthMax ?? ''}
                    onChange={(e) => setBbWidthMax(e.target.value === '' ? undefined : Number(e.target.value))}
                    placeholder="0.15"
                    min="0"
                    max="1"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                  />
                  <div className="text-white/50 text-xs mt-1">要求布林带宽度必须小于等于此值（检测压缩，默认15%）</div>
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">成交量比率最小值（倍）</label>
                  <input
                    type="number"
                    step="0.1"
                    value={volumeRatioMin ?? ''}
                    onChange={(e) => setVolumeRatioMin(e.target.value === '' ? undefined : Number(e.target.value))}
                    placeholder="1.5"
                    min="0"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                  />
                  <div className="text-white/50 text-xs mt-1">
                    要求当前成交量必须大于等于过去20根K线平均成交量的此倍数（检测点火，默认1.5倍）
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="flex items-center text-white/70 text-sm mb-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={priceAboveEma}
                      onChange={(e) => setPriceAboveEma(e.target.checked)}
                      className="mr-2 w-4 h-4 bg-white/10 border border-white/20 rounded text-white focus:ring-2 focus:ring-white/30"
                    />
                    要求价格在EMA之上（避免在熊市中接盘）
                  </label>
                  <div className="text-white/50 text-xs mt-1 ml-6">统一使用EMA50作为趋势线</div>
                </div>
              </div>
            </div>

            {/* 扫描按钮 */}
            <button
              onClick={handleScan}
              disabled={loading || backtesting || optimizing}
              className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>扫描中...</span>
                </>
              ) : (
                <span>扫描</span>
              )}
            </button>

            {/* 回测按钮 */}
            <button
              onClick={handleBacktest}
              disabled={loading || backtesting || optimizing}
              className="w-full py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white font-semibold rounded-xl hover:from-green-600 hover:to-teal-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {backtesting ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>回测中...</span>
                </>
              ) : (
                <span>回测</span>
              )}
            </button>

            {/* 优化按钮 */}
            <button
              onClick={handleOptimize}
              disabled={loading || backtesting || optimizing}
              className="w-full py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {optimizing ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>优化中...</span>
                </>
              ) : (
                <span>优化</span>
              )}
            </button>
          </div>

          {/* 回测结果 */}
          {backtestResult && (
            <div className="bg-white/5 border border-white/20 rounded-xl p-4 space-y-2 mb-4">
              <div className="text-white/90 font-semibold">回测任务</div>
              <div className="text-white/70 text-sm">任务ID: {backtestResult.taskId}</div>
              <div className="text-white/70 text-sm">
                状态:{' '}
                {backtestResult.status === 'pending'
                  ? '等待中'
                  : backtestResult.status === 'running'
                    ? '处理中'
                    : backtestResult.status === 'completed'
                      ? '已完成'
                      : '失败'}
              </div>
              <div className="text-white/70 text-sm">{backtestResult.message}</div>
              {backtestResult.result && (
                <div className="mt-3 space-y-1">
                  <div className="text-white/70 text-sm">总信号数: {backtestResult.result.totalSignals}</div>
                  <div className="text-white/70 text-sm">盈利信号数: {backtestResult.result.profitableSignals}</div>
                  <div className="text-green-400 font-semibold text-sm">
                    成功率: {backtestResult.result.successRate}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 优化结果 */}
          {optimizeResult && (
            <div className="bg-white/5 border border-white/20 rounded-xl p-4 space-y-2 mb-4">
              <div className="text-white/90 font-semibold">优化任务</div>
              <div className="text-white/70 text-sm">任务ID: {optimizeResult.taskId}</div>
              <div className="text-white/70 text-sm">
                状态:{' '}
                {optimizeResult.status === 'pending'
                  ? '等待中'
                  : optimizeResult.status === 'running'
                    ? '处理中'
                    : optimizeResult.status === 'completed'
                      ? '已完成'
                      : '失败'}
              </div>
              <div className="text-white/70 text-sm">{optimizeResult.message}</div>
              {optimizeResult.result && (
                <div className="mt-3 space-y-2">
                  <div className="text-green-400 font-semibold text-sm">
                    最佳成功率: {optimizeResult.result.bestSuccessRate}
                  </div>
                  <div className="text-white/70 text-sm">迭代次数: {optimizeResult.result.iterations}</div>
                  {optimizeResult.result.bestParams && (
                    <div className="mt-2 pt-2 border-t border-white/10">
                      <div className="text-white/80 text-sm font-semibold mb-1">优化后的参数:</div>
                      <div className="text-white/60 text-xs space-y-1">
                        <div>
                          价格斜率范围: {optimizeResult.result.bestParams.priceSlopeMin} ~{' '}
                          {optimizeResult.result.bestParams.priceSlopeMax}
                        </div>
                        {optimizeResult.result.bestParams.rsiSlopeMin !== undefined && (
                          <div>
                            RSI斜率范围: {optimizeResult.result.bestParams.rsiSlopeMin} ~{' '}
                            {optimizeResult.result.bestParams.rsiSlopeMax}
                          </div>
                        )}
                        {optimizeResult.result.bestParams.volumeSlopeMin !== undefined && (
                          <div>
                            成交量斜率范围: {optimizeResult.result.bestParams.volumeSlopeMin} ~{' '}
                            {optimizeResult.result.bestParams.volumeSlopeMax}
                          </div>
                        )}
                        {optimizeResult.result.bestParams.obvSlopeMin !== undefined && (
                          <div>OBV斜率最小值: {optimizeResult.result.bestParams.obvSlopeMin}</div>
                        )}
                        {optimizeResult.result.bestParams.priceAboveEma !== undefined && (
                          <div>价格在EMA之上: {optimizeResult.result.bestParams.priceAboveEma ? '是' : '否'}</div>
                        )}
                        {optimizeResult.result.bestParams.rsiMin !== undefined && (
                          <div>RSI最小值: {optimizeResult.result.bestParams.rsiMin}</div>
                        )}
                        {optimizeResult.result.bestParams.rsiMax !== undefined && (
                          <div>RSI最大值: {optimizeResult.result.bestParams.rsiMax}</div>
                        )}
                        {optimizeResult.result.bestParams.bbWidthMax !== undefined && (
                          <div>布林带宽度最大值: {optimizeResult.result.bestParams.bbWidthMax}</div>
                        )}
                        {optimizeResult.result.bestParams.volumeRatioMin !== undefined && (
                          <div>成交量比率最小值: {optimizeResult.result.bestParams.volumeRatioMin}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 结果列表 */}
          {signals.length > 0 && (
            <div className="space-y-4">
              <div className="text-white/90 font-semibold">扫描结果 ({signals.length} 个信号)</div>
              {signals.map((signal, index) => (
                <div key={index} className="bg-white/5 border border-white/20 rounded-xl p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-white font-semibold">{signal.symbol}</div>
                    <div className="text-white/60 text-sm">#{index + 1}</div>
                  </div>
                  <div className="text-white/70 text-sm">
                    时间段: {formatTimestamp(signal.startTime)} 至 {formatTimestamp(signal.endTime)}
                  </div>
                  <div className="text-white/70 text-sm">
                    信号类型: {signal.result.signalType === 'ABSORPTION' ? '强力吸筹' : signal.result.signalType}
                  </div>
                  <div className="text-white/70 text-sm">价格斜率: {signal.result.priceSlope.toFixed(4)}</div>
                  <div className="text-white/70 text-sm">RSI斜率: {signal.result.rsiSlope.toFixed(4)}</div>
                  <div className="text-white/70 text-sm">成交量斜率: {signal.result.volumeSlope.toFixed(4)}</div>
                  <div className="text-white/70 text-sm">OBV斜率: {signal.result.obvSlope.toFixed(4)}</div>
                  <div className="text-white/70 text-sm">当前RSI: {signal.result.rsiValue.toFixed(2)}</div>
                  <div className="text-white/80 text-sm mt-2 p-2 bg-white/5 rounded">{signal.result.reason}</div>
                </div>
              ))}
            </div>
          )}

          {!loading && signals.length === 0 && (
            <div className="text-center text-white/60 py-8">暂无信号，点击"扫描"进行扫描</div>
          )}
        </div>
      </div>
    </GradientBackground>
  );
};

export default SignalScanner;
